/**
 * Atendimentos: status, choques de horário e conclusão (plano, seções 16.3, 17.3 e 19.3).
 *
 *   pendente (com aprovação) → agendado → confirmado → concluído
 *                                  │           │
 *                                  ├→ cancelado ←┘
 *                                  └→ faltou
 *
 * - concluído não volta atrás (só por estorno explícito);
 * - faltou conta para o histórico do cliente.
 */
import type { Appointment, AppointmentStatus } from '../schemas/entities';
import { isBusyStatus } from './availability';
import { MINUTE, overlaps, toIso, toMs } from './time';

const TRANSITIONS: Record<AppointmentStatus, readonly AppointmentStatus[]> = {
  pending: ['scheduled', 'canceled'],
  scheduled: ['confirmed', 'completed', 'canceled', 'no_show'],
  confirmed: ['scheduled', 'completed', 'canceled', 'no_show'],
  // Só por estorno explícito (reverseCompletion)
  completed: [],
  // "Desfazer" logo depois de cancelar (ou recusar um pedido) ou marcar falta
  canceled: ['scheduled', 'pending'],
  no_show: ['scheduled'],
};

export function canTransition(from: AppointmentStatus, to: AppointmentStatus): boolean {
  return from === to || TRANSITIONS[from].includes(to);
}

export const STATUS_LABELS: Record<AppointmentStatus, string> = {
  pending: 'Aguardando aprovação',
  scheduled: 'Agendado',
  confirmed: 'Confirmado',
  completed: 'Concluído',
  canceled: 'Cancelado',
  no_show: 'Faltou',
};

export function isActive(a: Pick<Appointment, 'status' | 'deleted_at'>): boolean {
  return !a.deleted_at && isBusyStatus(a.status) && a.status !== 'completed';
}

/** Ocupa a agenda (inclui concluídos). */
export function occupiesTime(a: Pick<Appointment, 'status' | 'deleted_at'>): boolean {
  return !a.deleted_at && isBusyStatus(a.status);
}

export function endFromStart(startIso: string, durationMin: number): string {
  return toIso(toMs(startIso) + durationMin * MINUTE);
}

export function durationMinOf(a: Pick<Appointment, 'start_utc' | 'end_utc'>): number {
  return Math.round((toMs(a.end_utc) - toMs(a.start_utc)) / MINUTE);
}

/**
 * Valor a cobrar (preço menos desconto).
 *
 * O pacote entra como desconto do preço do serviço que ele cobre, e não
 * zerando o atendimento: um atendimento pode ter vários serviços, e o pacote
 * é sempre de um só. Quem marca "pé e mão" com pacote mais "esmaltação" paga
 * a esmaltação.
 *
 * O caso de `package_id` com desconto zerado é legado: antes de a cobertura
 * virar desconto, era assim que se dizia "o pacote pagou tudo". Uma migration
 * corrigiu o que estava gravado, mas um aparelho que passou dias sem
 * sincronizar ainda tem a versão antiga -- e mostrar que ela deve o valor
 * inteiro seria pior do que manter o comportamento de antes até o dado
 * chegar.
 */
export function amountDue(a: Pick<Appointment, 'price_cents' | 'discount_cents' | 'package_id'>): number {
  if (a.package_id && a.discount_cents === 0 && a.price_cents > 0) return 0;
  return Math.max(0, a.price_cents - a.discount_cents);
}

export type OverlapPair = [Appointment, Appointment];

/**
 * Pares de atendimentos que se sobrepõem (choque de horário).
 * Ignora os que a profissional decidiu manter (overlap_ok nos dois).
 */
export function findOverlaps(appointments: Appointment[]): OverlapPair[] {
  const live = appointments
    .filter((a) => occupiesTime(a))
    .sort((a, b) => toMs(a.start_utc) - toMs(b.start_utc));
  const pairs: OverlapPair[] = [];
  for (let i = 0; i < live.length; i++) {
    const a = live[i]!;
    const aEnd = toMs(a.end_utc);
    for (let j = i + 1; j < live.length; j++) {
      const b = live[j]!;
      const bStart = toMs(b.start_utc);
      if (bStart >= aEnd) break;
      if (a.overlap_ok && b.overlap_ok) continue;
      if (overlaps(toMs(a.start_utc), aEnd, bStart, toMs(b.end_utc))) pairs.push([a, b]);
    }
  }
  return pairs;
}

/** Atendimentos que batem com um horário proposto. */
export function conflictsWith(
  appointments: Appointment[],
  startIso: string,
  endIso: string,
  ignoreId?: string,
): Appointment[] {
  const s = toMs(startIso);
  const e = toMs(endIso);
  return appointments.filter(
    (a) => a.id !== ignoreId && occupiesTime(a) && overlaps(s, e, toMs(a.start_utc), toMs(a.end_utc)),
  );
}

/** Ids com choque (para pintar o aviso no cartão). */
export function conflictIds(appointments: Appointment[]): Set<string> {
  const ids = new Set<string>();
  for (const [a, b] of findOverlaps(appointments)) {
    ids.add(a.id);
    ids.add(b.id);
  }
  return ids;
}
